import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, X, Eye, Edit, Trash2, ClipboardList, ChevronLeft, Save,
  DollarSign, Globe, Clock, CheckCircle, AlertCircle, FileText, Package,
  TrendingUp, Filter, Ship,
} from 'lucide-react';
import { orderService } from '../services/orderService';
import { api } from '../config/api';

// ─── Status ────────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  en_revision: {
    label: 'En revisión',
    bg: 'bg-amber-100 text-amber-800 border-amber-300',
    icon: Clock,
  },
  po_enviada: {
    label: 'PO enviada',
    bg: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    icon: CheckCircle,
  },
};

const CURRENCY_OPTIONS = ['USD', 'EUR', 'CNY', 'ARS', 'BRL'];

// Lista desplegable de países de destino más comunes
const COUNTRY_OPTIONS = [
  'Argentina', 'Brasil', 'Bolivia', 'Chile', 'Colombia', 'Costa Rica',
  'Cuba', 'Ecuador', 'El Salvador', 'España', 'Estados Unidos', 'Guatemala',
  'Honduras', 'México', 'Nicaragua', 'Panamá', 'Paraguay', 'Perú',
  'Puerto Rico', 'República Dominicana', 'Uruguay', 'Venezuela',
];

// ─── Helpers ───────────────────────────────────────────────────────────────
const fmtN = (v, d = 2) => {
  const n = parseFloat(v);
  if (isNaN(n)) return '-';
  return n.toLocaleString('es-AR', { minimumFractionDigits: d, maximumFractionDigits: d });
};

const fmtDate = (v) => {
  if (!v) return '-';
  try { return new Date(v + 'T00:00:00').toLocaleDateString('es-AR'); } catch { return v; }
};

// ─── Initial states ────────────────────────────────────────────────────────
const INITIAL_ORDER = {
  status: 'en_revision',
  client: '',
  customer_order: '',
  customer_order_ref: '',
  buyer: '',
  client_pi: '',
  pi_issued_by: '',
  order_date: '',
  shipping_date: '',
  delivery_date: '',
  loading_port: '',
  destination_country: '',
  currency: 'USD',
  description: '',
  comments: '',
  inspection_required: false,
  payment_terms: '',
  rate_discount: '',
  rebate: '',
  balance_due: '',
  trading_fob_cost: '',
  operational_cost: '',
};

const INITIAL_ITEM = {
  product: '',
  agent: '',
  factory: '',
  factory_port: '',
  product_variant: '',
  product_type: '',
  item_code: '',
  product_image: '',
  customer_item_code: '',
  color_assortment: '',
  artwork: '',
  customer_battery_info: '',
  battery_fty_info: '',
  factory_item_code: '',
  factory_po: '',
  upc_ean13: '',
  incoterm: '',
  brand: '',
  category_name: '',
  description: '',
  unit_of_measure: '',
  en71_available: false,
  order_pcs: '',
  delivered_pieces: '',
  delivered_cartons: '',
  unit_price: '',
  factory_cost: '',
  current_cost: '',
  final_cost: '',
  discount: '',
  currency: '',
  pcs_per_ctn: '',
  inners: '',
  cbm_per_ctn: '',
  fty_pcs_per_ctn: '',
  fty_cbm_per_ctn: '',
  total_cartons: '',
  total_cbm: '',
  total_cost_rmb: '',
  extra_charge_orilat: '',
  extra_charge_customer: '',
  unit_usd_extra_charge: '',
  customer_extra_unit_price: '',
  product_fob_cost: '',
  trading_fob_cost: '',
  rebate_unit_cost: '',
  final_unit_cost: '',
  product_fob_price: '',
  final_product_fob_price: '',
  total_fob_cost: '',
  total_fob_price: '',
  total_final_cost_usd: '',
  total_final_cost_rmb: '',
  margin: '',
  gross_profit: '',
  order_responsible: '',
  order_representative: '',
  comments: '',
};

// ─── Editor Tabs ───────────────────────────────────────────────────────────
const EDITOR_TABS = [
  { id: 'orden',    label: 'Información de la Orden' },
  { id: 'cliente',  label: 'Información del Cliente' },
  { id: 'totales',  label: 'PO Total' },
  { id: 'detalle',  label: 'Detalle por Producto' },
];

// ══════════════════════════════════════════════════════════════════════════════
// ORDER EDITOR COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const OrderEditor = ({
  order,                  // null → new order
  contacts = [],
  ports = [],
  onBack,
  onSaved,
  theme = 'light',
}) => {
  const isDark = theme === 'dark';
  const clientContacts = useMemo(() => contacts.filter(c => c.contactType === 'CLIENT'), [contacts]);
  const factoryContacts = useMemo(() => contacts.filter(c => c.contactType === 'FACTORY'), [contacts]);
  const supplierContacts = useMemo(() => contacts.filter(c => c.contactType === 'SUPPLIER'), [contacts]);

  const [activeTab, setActiveTab]       = useState('orden');
  const [orderData, setOrderData]       = useState(order || INITIAL_ORDER);
  const [items, setItems]               = useState([]);
  const [saving, setSaving]             = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);

  // Item modal state
  const [editingItem, setEditingItem]   = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [itemForm, setItemForm]         = useState({ ...INITIAL_ITEM });
  const [savingItem, setSavingItem]     = useState(false);
  const [deleteItem, setDeleteItem]     = useState(null);

  // Generate POs modal
  const [showGeneratePO, setShowGeneratePO] = useState(false);
  const [groupByFactory, setGroupByFactory] = useState(true);
  const [generatingPO, setGeneratingPO]     = useState(false);

  // Product search within item modal
  const [productSearch, setProductSearch]     = useState('');
  const [productResults, setProductResults]   = useState([]);
  const [searchingProduct, setSearchingProduct] = useState(false);

  const isNew = !order;

  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed] placeholder-[#9aa0a6]'
           : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-[#bdc1c6]' : 'text-gray-600'}`;
  const sText    = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText    = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls  = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;

  // Load items when editing
  useEffect(() => {
    if (!order?.id) return;
    setLoadingItems(true);
    orderService.getFullOrderItems(order.id)
      .then(data => setItems(Array.isArray(data) ? data : data?.results || []))
      .catch(console.error)
      .finally(() => setLoadingItems(false));
  }, [order?.id]);

  const set = useCallback((field, value) => {
    setOrderData(prev => ({ ...prev, [field]: value }));
  }, []);

  // Totals computed from items + manual costs
  const totals = useMemo(() => {
    const total_pieces            = items.reduce((s, it) => s + (parseInt(it.order_pcs) || 0), 0);
    const total_delivered_pieces  = items.reduce((s, it) => s + (parseInt(it.delivered_pieces) || 0), 0);
    const total_cartons           = items.reduce((s, it) => s + (parseInt(it.total_cartons) || 0), 0);
    const total_delivered_cartons = items.reduce((s, it) => s + (parseInt(it.delivered_cartons) || 0), 0);
    const total_cbm               = items.reduce((s, it) => s + (parseFloat(it.total_cbm) || 0), 0);
    const total_fob_cost          = items.reduce((s, it) => s + (parseFloat(it.total_fob_cost) || 0), 0);
    const total_fob_price         = items.reduce((s, it) => s + (parseFloat(it.total_fob_price) || 0), 0);
    const extra_orilat            = items.reduce((s, it) => s + (parseFloat(it.extra_charge_orilat) || 0), 0);
    const extra_customer          = items.reduce((s, it) => s + (parseFloat(it.extra_charge_customer) || 0), 0);
    const margin = total_fob_cost > 0 ? ((total_fob_price / total_fob_cost - 1) * 100) : 0;
    return {
      total_pieces, total_delivered_pieces, total_cartons, total_delivered_cartons,
      total_cbm, total_fob_cost, total_fob_price, extra_orilat, extra_customer, margin,
    };
  }, [items]);

  // ── Save full order ──────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!orderData.client) return;
    setSaving(true);
    try {
      const payload = { ...orderData };
      ['rebate', 'rate_discount', 'balance_due', 'trading_fob_cost', 'operational_cost'].forEach(k => {
        payload[k] = payload[k] === '' ? null : (parseFloat(payload[k]) || null);
      });
      ['order_date', 'shipping_date', 'delivery_date'].forEach(k => {
        if (!payload[k]) payload[k] = null;
      });
      if (!payload.loading_port) payload.loading_port = null;
      if (payload.client) payload.client = parseInt(payload.client, 10) || null;
      // customer_order FK queda como está (se setea por el sistema al generar desde un Pedido)
      if (payload.customer_order === '' || payload.customer_order === undefined) {
        delete payload.customer_order;
      }

      let saved;
      if (isNew) {
        saved = await orderService.createFullOrder(payload);
      } else {
        saved = await orderService.updateFullOrder(order.id, payload);
      }
      if (onSaved) onSaved(saved);
    } catch (err) {
      console.error('Error guardando orden completa:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Generate POs ─────────────────────────────────────────────────────────
  const handleGeneratePOs = async () => {
    if (!order?.id) return;
    setGeneratingPO(true);
    try {
      await orderService.generatePOs(order.id, groupByFactory);
      setShowGeneratePO(false);
      const fresh = await orderService.getFullOrder(order.id);
      if (onSaved) onSaved(fresh);
    } catch (err) {
      console.error('Error generando POs:', err);
    } finally {
      setGeneratingPO(false);
    }
  };

  // ── Product search ───────────────────────────────────────────────────────
  const searchProductRef = useRef(null);
  useEffect(() => {
    if (!productSearch || productSearch.length < 2) { setProductResults([]); return; }
    setSearchingProduct(true);
    clearTimeout(searchProductRef.current);
    searchProductRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/products/?search=${encodeURIComponent(productSearch)}&limit=15`);
        const list = Array.isArray(res) ? res : res?.results || [];
        setProductResults(list);
      } catch { setProductResults([]); }
      finally { setSearchingProduct(false); }
    }, 350);
  }, [productSearch]);

  const applyProduct = (p) => {
    setItemForm(prev => ({
      ...prev,
      product: p.id,
      product_variant: p.product_variant || p.name || '',
      item_code: p.product_code || p.code || '',
      product_image: p.main_image || '',
      brand: p.brand_name || (p.brand?.name) || '',
      category_name: p.category_name || (p.category?.name) || '',
      description: p.description || '',
      pcs_per_ctn: p.pcs_per_ctn || '',
      inners: p.inner_ctn || '',
      cbm_per_ctn: p.cbm_per_ctn || '',
      incoterm: p.incoterm || '',
      factory_cost: p.cost_fob || '',
      unit_price: p.price_fob || '',
      factory: p.factory_id || p.factory || '',
      factory_item_code: p.manufacturer_code || '',
    }));
    setProductSearch('');
    setProductResults([]);
  };

  // ── Item CRUD ────────────────────────────────────────────────────────────
  const openAddItem = () => {
    setEditingItem(null);
    setItemForm({ ...INITIAL_ITEM });
    setProductSearch('');
    setProductResults([]);
    setShowItemModal(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    const form = { ...INITIAL_ITEM };
    Object.keys(INITIAL_ITEM).forEach(k => {
      if (item[k] !== undefined && item[k] !== null) form[k] = item[k];
    });
    setItemForm(form);
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!order?.id) return;
    setSavingItem(true);
    try {
      const p = { ...itemForm };
      const intKeys = ['order_pcs', 'delivered_pieces', 'delivered_cartons',
                       'pcs_per_ctn', 'inners', 'fty_pcs_per_ctn', 'total_cartons'];
      const floatKeys = ['unit_price', 'factory_cost', 'current_cost', 'final_cost',
                         'discount', 'cbm_per_ctn', 'fty_cbm_per_ctn',
                         'total_cbm', 'total_cost_rmb',
                         'extra_charge_orilat', 'extra_charge_customer',
                         'unit_usd_extra_charge', 'customer_extra_unit_price',
                         'product_fob_cost', 'trading_fob_cost', 'rebate_unit_cost',
                         'final_unit_cost', 'product_fob_price', 'final_product_fob_price',
                         'total_fob_cost', 'total_fob_price',
                         'total_final_cost_usd', 'total_final_cost_rmb',
                         'margin', 'gross_profit'];
      intKeys.forEach(k => { p[k] = p[k] === '' ? null : (parseInt(p[k], 10) || null); });
      floatKeys.forEach(k => { p[k] = p[k] === '' ? null : (parseFloat(p[k]) || null); });
      if (!p.product) p.product = null;
      if (!p.factory) p.factory = null;
      if (!p.agent) p.agent = null;
      if (!p.factory_port) p.factory_port = null;

      if (editingItem) {
        await orderService.updateFullOrderItem(order.id, editingItem.id, p);
      } else {
        await orderService.createFullOrderItem(order.id, p);
      }
      setShowItemModal(false);
      const data = await orderService.getFullOrderItems(order.id);
      setItems(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error guardando ítem:', err);
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!order?.id) return;
    try {
      await orderService.deleteFullOrderItem(order.id, itemId);
      setItems(prev => prev.filter(it => it.id !== itemId));
    } catch (err) { console.error('Error eliminando ítem:', err); }
    setDeleteItem(null);
  };

  const setItem = (field, value) => setItemForm(prev => ({ ...prev, [field]: value }));

  // ── Refs para componentes estables (evitar remounting al tipear) ─────────
  const orderDataRef = useRef(orderData);
  const setRef       = useRef(set);
  const inputClsRef  = useRef(inputCls);
  const labelClsRef  = useRef(labelCls);
  orderDataRef.current = orderData;
  setRef.current       = set;
  inputClsRef.current  = inputCls;
  labelClsRef.current  = labelCls;

  const Field = useMemo(() => ({ label, children, span2 = false }) => (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <label className={labelClsRef.current}>{label}</label>
      {children}
    </div>
  ), []);

  const Input = useMemo(() => ({ field, type = 'text', placeholder = '', step }) => (
    <input
      type={type} step={step}
      className={inputClsRef.current}
      value={orderDataRef.current[field] ?? ''}
      onChange={e => setRef.current(field, e.target.value)}
      placeholder={placeholder}
    />
  ), []);

  // ── TAB: Información de la Orden ─────────────────────────────────────────
  const TabOrden = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Pedidos">
        <Input field="customer_order_ref" placeholder="Ej: PD-000001" />
      </Field>
      <Field label="Fecha de pedido">
        <Input field="order_date" type="date" />
      </Field>
      <Field label="Comerciante">
        <Input field="buyer" placeholder="Nombre del comprador/comerciante" />
      </Field>
      <Field label="PI del cliente">
        <Input field="client_pi" placeholder="Ej: PI-2025-001" />
      </Field>
      <Field label="Fecha de envío">
        <Input field="shipping_date" type="date" />
      </Field>
      <Field label="Puerto de carga">
        <select className={inputCls} value={orderData.loading_port || ''} onChange={e => set('loading_port', e.target.value)}>
          <option value="">— Sin definir —</option>
          {ports.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      <Field label="País de destino">
        <select className={inputCls} value={orderData.destination_country || ''} onChange={e => set('destination_country', e.target.value)}>
          <option value="">— Sin definir —</option>
          {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Ingrese la fecha de entrega">
        <Input field="delivery_date" type="date" />
      </Field>
      <Field label="Moneda">
        <select className={inputCls} value={orderData.currency} onChange={e => set('currency', e.target.value)}>
          {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Estado de la orden">
        <select className={inputCls} value={orderData.status} onChange={e => set('status', e.target.value)}>
          {Object.entries(STATUS_CONFIG).map(([v, cfg]) => (
            <option key={v} value={v}>{cfg.label}</option>
          ))}
        </select>
      </Field>
      <Field label="Descripción" span2>
        <textarea
          rows={3} className={inputCls}
          value={orderData.description || ''}
          onChange={e => set('description', e.target.value)}
          placeholder="Descripción del pedido..."
        />
      </Field>
      <Field label="Comments" span2>
        <textarea
          rows={3} className={inputCls}
          value={orderData.comments || ''}
          onChange={e => set('comments', e.target.value)}
          placeholder="Notas adicionales..."
        />
      </Field>
    </div>
  );

  // ── TAB: Información del Cliente ─────────────────────────────────────────
  const TabCliente = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <Field label="Cliente *">
        <select className={inputCls} value={orderData.client || ''} onChange={e => set('client', e.target.value)}>
          <option value="">Seleccionar cliente...</option>
          {clientContacts.map(c => (
            <option key={c.id} value={c.id}>
              {c.codigoContacto ? `${c.codigoContacto} — ` : ''}{c.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="PI Emitido por">
        <Input field="pi_issued_by" placeholder="Quién emitió el PI" />
      </Field>
      <Field label="Término de pago">
        <Input field="payment_terms" placeholder="Ej: 30% anticipo, 70% contra BL" />
      </Field>
      <Field label="Saldo pendiente">
        <Input field="balance_due" type="number" step="0.01" placeholder="0.00" />
      </Field>
      <Field label="Descuento de tarifa (%)">
        <Input field="rate_discount" type="number" step="0.01" placeholder="0.00" />
      </Field>
      <Field label="Rebaja (%)">
        <Input field="rebate" type="number" step="0.01" placeholder="0.00" />
      </Field>
      <Field label="Inspección solicitada">
        <select
          className={inputCls}
          value={orderData.inspection_required ? 'si' : 'no'}
          onChange={e => set('inspection_required', e.target.value === 'si')}
        >
          <option value="no">No</option>
          <option value="si">Sí</option>
        </select>
      </Field>
    </div>
  );

  // ── TAB: PO Total ────────────────────────────────────────────────────────
  const TabTotales = () => {
    const tradingCost = parseFloat(orderData.trading_fob_cost) || 0;
    const operativeCost = parseFloat(orderData.operational_cost) || 0;

    return (
      <div className="space-y-6">
        <div className={`${cardCls} p-5`}>
          <h4 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${pText}`}>
            <TrendingUp size={16} className="text-blue-500" />
            Totales calculados (a partir de los ítems)
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
            {[
              ['Total piezas del pedido',  fmtN(totals.total_pieces, 0), true],
              ['Cargos extra Orilat',      `USD ${fmtN(totals.extra_orilat)}`],
              ['Piezas entregadas',        fmtN(totals.total_delivered_pieces, 0)],
              ['Cargos extra cliente',     `USD ${fmtN(totals.extra_customer)}`],
              ['Total de cartones',        fmtN(totals.total_cartons, 0), true],
              ['Costo total FOB',          `USD ${fmtN(totals.total_fob_cost)}`],
              ['Cartones entregados',      fmtN(totals.total_delivered_cartons, 0)],
              ['Precio total FOB',         `USD ${fmtN(totals.total_fob_price)}`, true],
              ['Total CBM',                `${fmtN(totals.total_cbm, 3)} m³`, true],
              ['Margen',                   `${fmtN(totals.margin)} %`],
            ].map(([label, value, bold], i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
                <span className={`text-sm ${sText}`}>{label}</span>
                <span className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${pText}`}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`${cardCls} p-5`}>
          <h4 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${pText}`}>
            <DollarSign size={16} className="text-emerald-500" />
            Costos manuales
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Costo FOB de Comercio">
              <Input field="trading_fob_cost" type="number" step="0.01" placeholder="0.00" />
            </Field>
            <Field label="Costo Operativo">
              <Input field="operational_cost" type="number" step="0.01" placeholder="0.00" />
            </Field>
          </div>
          {(tradingCost > 0 || operativeCost > 0) && (
            <p className={`text-xs mt-3 ${sText}`}>
              Total costos manuales: <strong className={pText}>USD {fmtN(tradingCost + operativeCost)}</strong>
            </p>
          )}
        </div>

        {!order?.id && (
          <p className={`text-xs ${sText}`}>Los totales se calculan automáticamente desde los ítems luego de guardar la orden.</p>
        )}
      </div>
    );
  };

  // ── TAB: Detalle por Producto ────────────────────────────────────────────
  const TabDetalle = () => {
    const isExisting = !!order?.id;

    const cols = [
      { key: 'product_image',          label: 'Imagen',                w: 'w-16'  },
      { key: 'agent_name',             label: 'Proveedor',             w: 'w-28'  },
      { key: 'factory_name',           label: 'Fábrica',               w: 'w-28'  },
      { key: 'brand',                  label: 'Marca',                 w: 'w-24'  },
      { key: 'category_name',          label: 'Categoría',             w: 'w-28'  },
      { key: 'product_type',           label: 'Tipo Producto',         w: 'w-28'  },
      { key: 'item_code',              label: 'Cód. Artículo',         w: 'w-28'  },
      { key: 'product_variant',        label: 'Variante',              w: 'w-28'  },
      { key: 'customer_item_code',     label: 'Cód. Cliente',          w: 'w-28'  },
      { key: 'factory_item_code',      label: 'Cód. Fábrica',          w: 'w-28'  },
      { key: 'factory_po',             label: 'PO Fábrica',            w: 'w-24'  },
      { key: 'upc_ean13',              label: 'UPC/EAN13',             w: 'w-28'  },
      { key: 'incoterm',               label: 'Incoterm',              w: 'w-20'  },
      { key: 'unit_of_measure',        label: 'UoM',                   w: 'w-20'  },
      { key: 'currency',               label: 'Moneda',                w: 'w-20'  },
      { key: 'en71_available',         label: 'EN71',                  w: 'w-16'  },
      { key: 'order_pcs',              label: 'Piezas Pedido',         w: 'w-24'  },
      { key: 'delivered_pieces',       label: 'Piezas Entreg.',        w: 'w-24'  },
      { key: 'difference',             label: 'Diferencia',            w: 'w-24'  },
      { key: 'unit_price',             label: 'Precio Unit.',          w: 'w-24'  },
      { key: 'factory_cost',           label: 'Costo Fábrica',         w: 'w-24'  },
      { key: 'current_cost',           label: 'Costo Actual',          w: 'w-24'  },
      { key: 'final_cost',             label: 'Costo Final',           w: 'w-24'  },
      { key: 'discount',               label: 'Descuento',             w: 'w-20'  },
      { key: 'pcs_per_ctn',            label: 'PCS/CTN',               w: 'w-20'  },
      { key: 'inners',                 label: 'Inners',                w: 'w-20'  },
      { key: 'cbm_per_ctn',            label: 'CBM/CTN',               w: 'w-24'  },
      { key: 'fty_pcs_per_ctn',        label: 'FTY PCS/CTN',           w: 'w-24'  },
      { key: 'fty_cbm_per_ctn',        label: 'FTY CBM/CTN',           w: 'w-24'  },
      { key: 'total_cartons',          label: 'Total Cartones',        w: 'w-28'  },
      { key: 'delivered_cartons',      label: 'Cart. Entregados',      w: 'w-28'  },
      { key: 'total_cbm',              label: 'Total CBM',             w: 'w-24'  },
      { key: 'total_cost_rmb',         label: 'Costo Total RMB',       w: 'w-28'  },
      { key: 'total_fob_cost',         label: 'Costo Total FOB USD',   w: 'w-32'  },
      { key: 'total_final_cost_usd',   label: 'Costo Final Total USD', w: 'w-32'  },
      { key: 'total_final_cost_rmb',   label: 'Costo Final Total RMB', w: 'w-32'  },
      { key: 'extra_charge_orilat',    label: 'Cargo Extra Orilat',    w: 'w-28'  },
      { key: 'extra_charge_customer',  label: 'Cargo Extra Cliente',   w: 'w-28'  },
      { key: 'unit_usd_extra_charge',  label: 'Cargo Extra USD/U',     w: 'w-28'  },
      { key: 'customer_extra_unit_price', label: 'Cargo Extra Cliente/U', w: 'w-32' },
      { key: 'product_fob_cost',       label: 'Costo FOB Producto',    w: 'w-28'  },
      { key: 'trading_fob_cost',       label: 'Costo FOB Comercio',    w: 'w-28'  },
      { key: 'rebate_unit_cost',       label: 'Costo Unit. Rebate',    w: 'w-28'  },
      { key: 'final_unit_cost',        label: 'Costo Final Unit.',     w: 'w-28'  },
      { key: 'product_fob_price',      label: 'Precio FOB Producto',   w: 'w-28'  },
      { key: 'final_product_fob_price',label: 'Precio FOB Final',      w: 'w-28'  },
      { key: 'total_fob_price',        label: 'Precio Total FOB',      w: 'w-28'  },
      { key: 'margin',                 label: 'Margen',                w: 'w-20'  },
      { key: 'gross_profit',           label: 'Beneficio Bruto',       w: 'w-28'  },
      { key: 'order_responsible',      label: 'Responsable Pedido',    w: 'w-32'  },
      { key: 'order_representative',   label: 'Representante Pedido',  w: 'w-32'  },
      { key: 'color_assortment',       label: 'Surtido Colores',       w: 'w-32'  },
      { key: 'artwork',                label: 'Artwork',               w: 'w-32'  },
      { key: 'customer_battery_info',  label: 'Info Batería Cliente',  w: 'w-36'  },
      { key: 'battery_fty_info',       label: 'Info Batería Fábrica',  w: 'w-36'  },
      { key: 'description',            label: 'Descripción',           w: 'w-44'  },
      { key: 'comments',               label: 'Comentarios',           w: 'w-40'  },
    ];

    const renderCell = (item, key) => {
      if (key === 'product_image') {
        return item.product_image
          ? <img src={item.product_image} alt="" className="w-10 h-10 object-cover rounded-md" />
          : <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center"><Package size={14} className="text-gray-400" /></div>;
      }
      if (key === 'en71_available') {
        return item.en71_available
          ? <span className="text-emerald-600 text-xs font-semibold">Sí</span>
          : <span className="text-gray-400 text-xs">No</span>;
      }
      const numKeys = ['unit_price','factory_cost','current_cost','final_cost','discount',
                       'cbm_per_ctn','fty_cbm_per_ctn','total_cbm','total_cost_rmb',
                       'total_fob_cost','total_fob_price','total_final_cost_usd','total_final_cost_rmb',
                       'extra_charge_orilat','extra_charge_customer','unit_usd_extra_charge',
                       'customer_extra_unit_price','product_fob_cost','trading_fob_cost',
                       'rebate_unit_cost','final_unit_cost','product_fob_price',
                       'final_product_fob_price','gross_profit'];
      const intKeys = ['order_pcs','delivered_pieces','difference','pcs_per_ctn','inners',
                       'fty_pcs_per_ctn','total_cartons','delivered_cartons'];
      const pctKeys = ['margin'];
      const v = item[key];
      if (v == null || v === '') return <span className="text-gray-400">—</span>;
      if (pctKeys.includes(key)) return `${fmtN(v)}%`;
      if (numKeys.includes(key)) return fmtN(v);
      if (intKeys.includes(key)) return fmtN(v, 0);
      return String(v);
    };

    if (!isExisting) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package size={48} className="mb-3 text-gray-300" />
          <p className="text-sm text-gray-500 max-w-xs">
            Guardá primero la Orden Completa para poder agregar ítems al detalle.
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <p className={`text-sm ${sText}`}>{items.length} ítem{items.length !== 1 ? 's' : ''}</p>
          <button
            onClick={openAddItem}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <Plus size={15} /> Agregar Ítem
          </button>
        </div>

        {loadingItems ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 border border-dashed border-gray-200 rounded-xl">
            <Package size={40} className="mb-3 text-gray-300" />
            <p className="text-sm text-gray-500 mb-3">No hay ítems en esta orden</p>
            <button onClick={openAddItem} className="text-sm text-blue-600 hover:underline font-medium">Agregar el primer ítem</button>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {cols.map(c => (
                    <th key={c.key} className={`px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap ${c.w}`}>
                      {c.label}
                    </th>
                  ))}
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide w-20">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, idx) => (
                  <tr key={item.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                    {cols.map(c => (
                      <td key={c.key} className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
                        {renderCell(item, c.key)}
                      </td>
                    ))}
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEditItem(item)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" title="Editar">
                          <Edit size={13} className="text-blue-500" />
                        </button>
                        <button onClick={() => setDeleteItem(item)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" title="Eliminar">
                          <Trash2 size={13} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Item Modal ───────────────────────────────────────────────────────────
  const ItemModal = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className={`w-full max-w-4xl rounded-xl shadow-2xl border max-h-[90vh] flex flex-col ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`}>
        <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
          <h3 className={`text-lg font-semibold ${pText}`}>{editingItem ? 'Editar Ítem' : 'Agregar Ítem'}</h3>
          <button onClick={() => setShowItemModal(false)} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={20} className={sText} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Product search */}
          <div>
            <label className={labelCls}>Buscar producto del catálogo</label>
            <div className="relative">
              <Search size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${sText}`} />
              <input
                className={`${inputCls} pl-8`}
                value={productSearch}
                onChange={e => setProductSearch(e.target.value)}
                placeholder="Buscar por nombre o código..."
              />
              {searchingProduct && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
            </div>
            {productResults.length > 0 && (
              <div className="mt-1 border border-gray-200 rounded-lg shadow-lg bg-white max-h-40 overflow-y-auto">
                {productResults.map(p => (
                  <button
                    key={p.id}
                    onClick={() => applyProduct(p)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center gap-2 border-b border-gray-100 last:border-0"
                  >
                    {p.main_image && <img src={p.main_image} alt="" className="w-8 h-8 object-cover rounded" />}
                    <div>
                      <div className="font-medium text-gray-800">{p.name}</div>
                      <div className="text-xs text-gray-500">{p.product_code || p.code || ''}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sección: Identificación */}
          <div>
            <h4 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${sText}`}>Identificación</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Proveedor</label>
                <select className={inputCls} value={itemForm.agent || ''} onChange={e => setItem('agent', e.target.value)}>
                  <option value="">— Sin definir —</option>
                  {supplierContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Fábrica</label>
                <select className={inputCls} value={itemForm.factory || ''} onChange={e => setItem('factory', e.target.value)}>
                  <option value="">— Sin definir —</option>
                  {factoryContacts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Puerto de la Fábrica</label>
                <select className={inputCls} value={itemForm.factory_port || ''} onChange={e => setItem('factory_port', e.target.value)}>
                  <option value="">— Sin definir —</option>
                  {ports.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Marca</label>
                <input className={inputCls} value={itemForm.brand} onChange={e => setItem('brand', e.target.value)} placeholder="Marca" />
              </div>
              <div>
                <label className={labelCls}>Categoría del Producto</label>
                <input className={inputCls} value={itemForm.category_name} onChange={e => setItem('category_name', e.target.value)} placeholder="Categoría" />
              </div>
              <div>
                <label className={labelCls}>Tipo de Producto</label>
                <input className={inputCls} value={itemForm.product_type} onChange={e => setItem('product_type', e.target.value)} placeholder="Tipo" />
              </div>
              <div>
                <label className={labelCls}>Variante del Producto</label>
                <input className={inputCls} value={itemForm.product_variant} onChange={e => setItem('product_variant', e.target.value)} placeholder="Variante" />
              </div>
              <div>
                <label className={labelCls}>Imagen del Producto (URL)</label>
                <input className={inputCls} value={itemForm.product_image} onChange={e => setItem('product_image', e.target.value)} placeholder="https://..." />
              </div>
              <div>
                <label className={labelCls}>Código del Artículo</label>
                <input className={inputCls} value={itemForm.item_code} onChange={e => setItem('item_code', e.target.value)} placeholder="Código" />
              </div>
              <div>
                <label className={labelCls}>Código del Artículo del Cliente</label>
                <input className={inputCls} value={itemForm.customer_item_code} onChange={e => setItem('customer_item_code', e.target.value)} placeholder="Cód. cliente" />
              </div>
              <div>
                <label className={labelCls}>Código del Artículo de Fábrica</label>
                <input className={inputCls} value={itemForm.factory_item_code} onChange={e => setItem('factory_item_code', e.target.value)} placeholder="Cód. fábrica" />
              </div>
              <div>
                <label className={labelCls}>PO de la Fábrica</label>
                <input className={inputCls} value={itemForm.factory_po} onChange={e => setItem('factory_po', e.target.value)} placeholder="PO" />
              </div>
              <div>
                <label className={labelCls}>UPC / EAN13</label>
                <input className={inputCls} value={itemForm.upc_ean13} onChange={e => setItem('upc_ean13', e.target.value)} placeholder="UPC/EAN13" />
              </div>
              <div>
                <label className={labelCls}>Incoterm</label>
                <input className={inputCls} value={itemForm.incoterm} onChange={e => setItem('incoterm', e.target.value)} placeholder="FOB, CIF..." />
              </div>
              <div>
                <label className={labelCls}>Unidad de Medida</label>
                <input className={inputCls} value={itemForm.unit_of_measure} onChange={e => setItem('unit_of_measure', e.target.value)} placeholder="Unidad, Caja..." />
              </div>
              <div>
                <label className={labelCls}>Moneda</label>
                <select className={inputCls} value={itemForm.currency || ''} onChange={e => setItem('currency', e.target.value)}>
                  <option value="">— Heredar —</option>
                  {CURRENCY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="flex items-center pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox" className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={!!itemForm.en71_available}
                    onChange={e => setItem('en71_available', e.target.checked)}
                  />
                  <span className={`text-sm ${pText}`}>EN71 Disponible</span>
                </label>
              </div>
            </div>
          </div>

          {/* Sección: Cantidades */}
          <div>
            <h4 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${sText}`}>Cantidades y Empaque</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Piezas del Pedido</label>
                <input type="number" className={inputCls} value={itemForm.order_pcs} onChange={e => setItem('order_pcs', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Piezas Entregadas</label>
                <input type="number" className={inputCls} value={itemForm.delivered_pieces} onChange={e => setItem('delivered_pieces', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>PCS/CTN</label>
                <input type="number" className={inputCls} value={itemForm.pcs_per_ctn} onChange={e => setItem('pcs_per_ctn', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Inners</label>
                <input type="number" className={inputCls} value={itemForm.inners} onChange={e => setItem('inners', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>CBM/CTN</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.cbm_per_ctn} onChange={e => setItem('cbm_per_ctn', e.target.value)} placeholder="0.000" />
              </div>
              <div>
                <label className={labelCls}>FTY PCS/CTN (Piezas/Cartón Fábrica)</label>
                <input type="number" className={inputCls} value={itemForm.fty_pcs_per_ctn} onChange={e => setItem('fty_pcs_per_ctn', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>FTY CBM/CTN (CBM/Cartón Fábrica)</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.fty_cbm_per_ctn} onChange={e => setItem('fty_cbm_per_ctn', e.target.value)} placeholder="0.000" />
              </div>
              <div>
                <label className={labelCls}>Total de Cartones</label>
                <input type="number" className={inputCls} value={itemForm.total_cartons} onChange={e => setItem('total_cartons', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Cartones Entregados</label>
                <input type="number" className={inputCls} value={itemForm.delivered_cartons} onChange={e => setItem('delivered_cartons', e.target.value)} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Total de CBM</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.total_cbm} onChange={e => setItem('total_cbm', e.target.value)} placeholder="0.000" />
              </div>
            </div>
          </div>

          {/* Sección: Precios y Costos */}
          <div>
            <h4 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${sText}`}>Precios y Costos</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Precio Unitario</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.unit_price} onChange={e => setItem('unit_price', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo de la Fábrica</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.factory_cost} onChange={e => setItem('factory_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Actual</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.current_cost} onChange={e => setItem('current_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Final</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.final_cost} onChange={e => setItem('final_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Descuento (%)</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.discount} onChange={e => setItem('discount', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo FOB del Producto</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.product_fob_cost} onChange={e => setItem('product_fob_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo FOB de Comercio</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.trading_fob_cost} onChange={e => setItem('trading_fob_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo de Unidad con Reembolso</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.rebate_unit_cost} onChange={e => setItem('rebate_unit_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Final por Unidad</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.final_unit_cost} onChange={e => setItem('final_unit_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Precio FOB del Producto</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.product_fob_price} onChange={e => setItem('product_fob_price', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Precio FOB Final del Producto</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.final_product_fob_price} onChange={e => setItem('final_product_fob_price', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Total del Producto en USD</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_fob_cost} onChange={e => setItem('total_fob_cost', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Total del Producto en RMB</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_cost_rmb} onChange={e => setItem('total_cost_rmb', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Total Final del Producto en USD</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_final_cost_usd} onChange={e => setItem('total_final_cost_usd', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Costo Total Final del Producto en RMB</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_final_cost_rmb} onChange={e => setItem('total_final_cost_rmb', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Precio Total FOB</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.total_fob_price} onChange={e => setItem('total_fob_price', e.target.value)} placeholder="0.00" />
              </div>
            </div>
          </div>

          {/* Sección: Cargos extra y márgenes */}
          <div>
            <h4 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${sText}`}>Cargos extra y Margen</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Cargo Extra Orilat</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.extra_charge_orilat} onChange={e => setItem('extra_charge_orilat', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Cargo Extra al Cliente</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.extra_charge_customer} onChange={e => setItem('extra_charge_customer', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Cargo Extra por Unidad en USD</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.unit_usd_extra_charge} onChange={e => setItem('unit_usd_extra_charge', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Cargo Extra por Unidad del Cliente</label>
                <input type="number" step="0.000001" className={inputCls} value={itemForm.customer_extra_unit_price} onChange={e => setItem('customer_extra_unit_price', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Margen (%)</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.margin} onChange={e => setItem('margin', e.target.value)} placeholder="0.00" />
              </div>
              <div>
                <label className={labelCls}>Beneficio Bruto</label>
                <input type="number" step="0.01" className={inputCls} value={itemForm.gross_profit} onChange={e => setItem('gross_profit', e.target.value)} placeholder="0.00" />
              </div>
            </div>
          </div>

          {/* Sección: Otros datos */}
          <div>
            <h4 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${sText}`}>Otros datos</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Responsable del Pedido</label>
                <input className={inputCls} value={itemForm.order_responsible} onChange={e => setItem('order_responsible', e.target.value)} placeholder="Nombre" />
              </div>
              <div>
                <label className={labelCls}>Representante del Pedido</label>
                <input className={inputCls} value={itemForm.order_representative} onChange={e => setItem('order_representative', e.target.value)} placeholder="Nombre" />
              </div>
              <div>
                <label className={labelCls}>Surtido de Colores</label>
                <input className={inputCls} value={itemForm.color_assortment} onChange={e => setItem('color_assortment', e.target.value)} placeholder="Surtido" />
              </div>
              <div>
                <label className={labelCls}>Información de la Batería del Cliente</label>
                <input className={inputCls} value={itemForm.customer_battery_info} onChange={e => setItem('customer_battery_info', e.target.value)} placeholder="Info batería" />
              </div>
              <div>
                <label className={labelCls}>Información de la Batería de Fábrica</label>
                <input className={inputCls} value={itemForm.battery_fty_info} onChange={e => setItem('battery_fty_info', e.target.value)} placeholder="Info batería fábrica" />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Artwork</label>
                <textarea rows={2} className={inputCls} value={itemForm.artwork} onChange={e => setItem('artwork', e.target.value)} placeholder="Instrucciones de arte/packaging..." />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Descripción</label>
                <textarea rows={2} className={inputCls} value={itemForm.description} onChange={e => setItem('description', e.target.value)} placeholder="Descripción del producto..." />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Comentarios</label>
                <textarea rows={2} className={inputCls} value={itemForm.comments} onChange={e => setItem('comments', e.target.value)} placeholder="Comentarios..." />
              </div>
            </div>
          </div>
        </div>

        <div className={`flex justify-end gap-3 p-5 border-t ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
          <button onClick={() => setShowItemModal(false)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">
            Cancelar
          </button>
          <button onClick={handleSaveItem} disabled={savingItem} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50">
            <Save size={15} />
            {savingItem ? 'Guardando...' : 'Guardar Ítem'}
          </button>
        </div>
      </div>
    </div>
  );

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <>
    <div className={`${cardCls}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`}
          >
            <ChevronLeft size={18} className={sText} />
          </button>
          <div>
            <h2 className={`text-lg font-bold ${pText}`}>
              {isNew ? 'Nueva Orden Completa' : orderData.order_code || 'Orden Completa'}
            </h2>
            {!isNew && orderData.client && (
              <p className={`text-xs ${sText}`}>
                {clientContacts.find(c => String(c.id) === String(orderData.client))?.name
                  || orderData.client_name
                  || `Cliente #${orderData.client}`}
              </p>
            )}
          </div>
          {!isNew && (() => {
            const cfg = STATUS_CONFIG[orderData.status];
            if (!cfg) return null;
            const Icon = cfg.icon;
            return (
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
                <Icon size={12} />{cfg.label}
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              onClick={() => setShowGeneratePO(true)}
              className="flex items-center gap-2 px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium"
            >
              <Ship size={15} />
              Generar POs
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !orderData.client}
            className="flex items-center gap-2 px-5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-all"
          >
            <Save size={15} />
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Horizontal tabs */}
      <div className={`flex gap-1 px-6 pt-3 border-b ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
        {EDITOR_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-blue-600 text-blue-600'
                : isDark
                  ? 'border-transparent text-[#bdc1c6] hover:text-[#e8eaed] hover:border-[#5f6368]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {activeTab === 'orden'   && TabOrden()}
        {activeTab === 'cliente' && TabCliente()}
        {activeTab === 'totales' && TabTotales()}
        {activeTab === 'detalle' && TabDetalle()}
      </div>
    </div>

    {/* Item Modal */}
    {showItemModal && ItemModal()}

    {/* Delete Item Confirm */}
    {deleteItem && (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center border border-gray-200">
          <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle size={24} className="text-red-600" />
          </div>
          <h3 className="text-lg font-semibold mb-2 text-gray-900">Eliminar ítem</h3>
          <p className="text-sm text-gray-500 mb-6">¿Eliminar <strong>{deleteItem.product_variant || deleteItem.item_code || 'este ítem'}</strong>?</p>
          <div className="flex justify-center gap-3">
            <button onClick={() => setDeleteItem(null)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">Cancelar</button>
            <button onClick={() => handleDeleteItem(deleteItem.id)} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium">
              <Trash2 size={15} /> Eliminar
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Generate POs Modal */}
    <AnimatePresence>
      {showGeneratePO && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowGeneratePO(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={e => e.stopPropagation()}
            className={`w-full max-w-md rounded-xl shadow-2xl border ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`}
          >
            <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${pText}`}>Generar Órdenes de Compra</h3>
              <button onClick={() => setShowGeneratePO(false)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`}>
                <X size={20} className={sText} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className={`text-sm ${sText}`}>
                Se generarán las POs a partir de los ítems de la orden{' '}
                <strong className={pText}>{orderData.order_code}</strong>.
              </p>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox" checked={groupByFactory}
                  onChange={e => setGroupByFactory(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className={`text-sm ${pText}`}>Agrupar por fábrica</span>
              </label>
            </div>
            <div className={`flex justify-end gap-3 p-5 border-t ${isDark ? 'border-[#3c4043]' : 'border-gray-200'}`}>
              <button onClick={() => setShowGeneratePO(false)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">
                Cancelar
              </button>
              <button onClick={handleGeneratePOs} disabled={generatingPO} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50">
                <Ship size={15} /> {generatingPO ? 'Generando...' : 'Generar POs'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════
const FullOrderManager = ({ theme = 'light', contacts = [] }) => {
  const isDark = theme === 'dark';

  const [orders, setOrders]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [activeOrder, setActiveOrder] = useState(null); // null → list, object → editor
  const [isNew, setIsNew]             = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting]         = useState(false);

  const [ports, setPorts]                 = useState([]);

  const sText  = isDark ? 'text-[#bdc1c6]' : 'text-gray-600';
  const pText  = isDark ? 'text-[#e8eaed]' : 'text-gray-900';
  const cardCls = `rounded-xl border shadow-sm ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`;
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
    isDark ? 'bg-[#202124] border-[#3c4043] text-[#e8eaed]' : 'bg-white border-gray-300 text-gray-900'
  }`;

  // Load orders
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await orderService.getFullOrders(params);
      setOrders(Array.isArray(data) ? data : data?.results || []);
    } catch (err) {
      console.error('Error cargando órdenes completas:', err);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, statusFilter]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Load ports once
  useEffect(() => {
    api.get('/pricing/ports/')
      .then(data => setPorts(Array.isArray(data) ? data : data?.results || []))
      .catch(console.error);
  }, []);

  const openOrder = useCallback(async (orderId) => {
    const detail = await orderService.getFullOrder(orderId);
    setActiveOrder(detail);
    setIsNew(false);
  }, []);

  const openNew = useCallback(() => {
    setActiveOrder({});
    setIsNew(true);
  }, []);

  const handleBack = useCallback(() => {
    setActiveOrder(null);
    setIsNew(false);
    loadOrders();
  }, [loadOrders]);

  const handleSaved = useCallback(async (saved) => {
    await loadOrders();
    if (saved?.id) {
      const detail = await orderService.getFullOrder(saved.id);
      setActiveOrder(detail);
      setIsNew(false);
    }
  }, [loadOrders]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await orderService.deleteFullOrder(deleteTarget.id);
      if (activeOrder?.id === deleteTarget.id) setActiveOrder(null);
      setDeleteTarget(null);
      await loadOrders();
    } catch (err) { console.error('Error eliminando orden completa:', err); }
    finally { setDeleting(false); }
  }, [deleteTarget, activeOrder, loadOrders]);

  const filteredOrders = useMemo(() => orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return [o.order_code, o.client_name, o.buyer, o.destination_country, o.client_pi]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    }
    return true;
  }), [orders, statusFilter, searchTerm]);

  const stats = useMemo(() => ({
    total:      orders.length,
    enRevision: orders.filter(o => o.status === 'en_revision').length,
    poEnviada:  orders.filter(o => o.status === 'po_enviada').length,
    totalFob:   orders.reduce((s, o) => s + (parseFloat(o.total_fob_price) || 0), 0),
  }), [orders]);

  const renderStatusBadge = (status) => {
    const cfg = STATUS_CONFIG[status];
    if (!cfg) return <span className="text-gray-400 text-xs">{status}</span>;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg}`}>
        <Icon size={12} />{cfg.label}
      </span>
    );
  };

  // ── Editor view ──────────────────────────────────────────────────────────
  if (activeOrder !== null) {
    return (
      <OrderEditor
        order={isNew ? null : activeOrder}
        contacts={contacts}
        ports={ports}
        theme={theme}
        onBack={handleBack}
        onSaved={handleSaved}
      />
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${pText}`}>Orden Completa</h1>
          <p className={`text-sm mt-1 ${sText}`}>Gestión de órdenes completas y generación de POs</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-500/20 text-sm font-medium"
        >
          <Plus size={18} /> Nueva Orden
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Órdenes', value: stats.total,      icon: ClipboardList, color: 'blue' },
          { label: 'En Revisión',   value: stats.enRevision, icon: Clock,         color: 'amber' },
          { label: 'PO Enviada',    value: stats.poEnviada,  icon: CheckCircle,   color: 'emerald' },
          { label: 'Total FOB',     value: `$${fmtN(stats.totalFob)}`, icon: DollarSign, color: 'purple' },
        ].map(s => (
          <div key={s.label} className={cardCls + ' p-4'}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium uppercase tracking-wider ${sText}`}>{s.label}</span>
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${s.color}-100`}>
                <s.icon size={16} className={`text-${s.color}-600`} />
              </div>
            </div>
            <p className={`text-xl font-bold ${pText}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className={cardCls + ' p-4'}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${sText}`} />
            <input
              type="text" placeholder="Buscar por código, cliente, comprador, PI..."
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              className={`${inputCls} pl-9`}
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X size={14} className={sText} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Filter size={16} className={sText} />
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`${inputCls} w-auto min-w-[180px]`}>
              <option value="all">Todos los estados</option>
              {Object.entries(STATUS_CONFIG).map(([v, cfg]) => (
                <option key={v} value={v}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className={cardCls + ' overflow-hidden'}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto" />
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <ClipboardList size={48} className={`mb-4 ${isDark ? 'text-[#5f6368]' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-semibold mb-1 ${pText}`}>Sin órdenes</h3>
            <p className={`text-sm mb-4 ${sText}`}>
              {searchTerm || statusFilter !== 'all' ? 'Sin resultados para los filtros actuales' : 'Todavía no hay órdenes completas creadas'}
            </p>
            {!searchTerm && statusFilter === 'all' && (
              <button onClick={openNew} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium">
                <Plus size={16} /> Crear primera orden
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'bg-[#202124]' : 'bg-gray-50'}>
                  {['Código', 'Estado', 'Cliente', 'Comerciante', 'PI Cliente', 'Fecha Pedido', 'País Destino', 'POs', 'Total FOB', 'Margen', 'Acciones'].map(h => (
                    <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider ${sText}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-[#3c4043]' : 'divide-gray-100'}`}>
                {filteredOrders.map((order, idx) => (
                  <tr
                    key={order.id}
                    onClick={() => openOrder(order.id)}
                    className={`transition-colors cursor-pointer ${
                      idx % 2 === 0
                        ? (isDark ? 'bg-[#292a2d]' : 'bg-white')
                        : (isDark ? 'bg-[#202124]' : 'bg-gray-50/50')
                    } ${isDark ? 'hover:bg-[#35363a]' : 'hover:bg-blue-50/30'}`}
                  >
                    <td className={`px-4 py-3 font-semibold ${pText}`}>
                      <div className="flex items-center gap-2">
                        <FileText size={14} className="text-blue-500" />
                        {order.order_code || `#${order.id}`}
                      </div>
                    </td>
                    <td className="px-4 py-3">{renderStatusBadge(order.status)}</td>
                    <td className={`px-4 py-3 ${pText}`}>{order.client_name || '-'}</td>
                    <td className={`px-4 py-3 ${sText}`}>{order.buyer || '-'}</td>
                    <td className={`px-4 py-3 ${sText}`}>{order.client_pi || '-'}</td>
                    <td className={`px-4 py-3 ${sText}`}>{fmtDate(order.order_date)}</td>
                    <td className={`px-4 py-3 ${sText}`}>
                      {order.destination_country
                        ? <span className="flex items-center gap-1"><Globe size={13} />{order.destination_country}</span>
                        : '-'}
                    </td>
                    <td className={`px-4 py-3 ${pText}`}>{order.po_count ?? '-'}</td>
                    <td className={`px-4 py-3 font-semibold ${pText}`}>{order.total_fob_price != null ? `$${fmtN(order.total_fob_price)}` : '-'}</td>
                    <td className={`px-4 py-3 ${pText}`}>{order.margin != null ? `${fmtN(order.margin)}%` : '-'}</td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openOrder(order.id)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`} title="Ver/Editar">
                          <Eye size={15} className="text-blue-500" />
                        </button>
                        <button onClick={() => setDeleteTarget(order)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-[#3c4043]' : 'hover:bg-gray-100'}`} title="Eliminar">
                          <Trash2 size={15} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {!loading && filteredOrders.length > 0 && (
        <p className={`text-xs ${sText}`}>Mostrando {filteredOrders.length} de {orders.length} órdenes</p>
      )}

      {/* Delete order confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-sm rounded-xl shadow-2xl border ${isDark ? 'bg-[#292a2d] border-[#3c4043]' : 'bg-white border-gray-200'}`}>
            <div className="p-6 text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <AlertCircle size={24} className="text-red-600" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${pText}`}>Confirmar eliminación</h3>
              <p className={`text-sm mb-6 ${sText}`}>
                ¿Eliminar la orden <strong>{deleteTarget.order_code || deleteTarget.id}</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200">Cancelar</button>
                <button onClick={handleDelete} disabled={deleting} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium disabled:opacity-50">
                  <Trash2 size={15} /> {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FullOrderManager;
